import type {MessageDescriptor} from 'react-intl';
import type {Call, CallParticipant, GroupCallOutcome} from '../../../store/features/calls/callsService';
import {groupCallMessages as m} from '../messages';
import {formatGroupCallDuration} from './GroupCallHistoryPresentation';

export type GroupCallParticipantStatus = 'started' | 'joined' | 'declined' | 'missed';

const OUTCOME_MESSAGES: Record<GroupCallOutcome, MessageDescriptor> = {
    OUTGOING: m.callsOutcomeOutgoing,
    OUTGOING_UNANSWERED: m.callsOutcomeOutgoingUnanswered,
    JOINED: m.callsOutcomeJoined,
    DECLINED: m.callsOutcomeDeclined,
    MISSED: m.callsOutcomeMissed,
};

const STATUS_MESSAGES: Record<GroupCallParticipantStatus, MessageDescriptor> = {
    started: m.callsDetailsStarted,
    joined: m.callsDetailsJoined,
    declined: m.callsDetailsDeclined,
    missed: m.callsDetailsMissed,
};

export const groupCallOutcomeMessage = (call: Pick<Call, 'myOutcome' | 'bridgetAt'>): MessageDescriptor =>
    OUTCOME_MESSAGES[call.myOutcome ?? (call.bridgetAt ? 'JOINED' : 'MISSED')];

export const isGroupCallOutcomeMissed = (call: Pick<Call, 'myOutcome'>): boolean =>
    call.myOutcome === 'MISSED' || call.myOutcome === 'DECLINED';

export const groupCallStarterId = (call: Pick<Call, 'user'>): number | undefined => call.user?.userId;

export const groupCallParticipantStatus = (call: Pick<Call, 'user'>, participant: CallParticipant): GroupCallParticipantStatus => {
    if (participant.userId === groupCallStarterId(call)) return 'started';
    if (participant.joinedAt) return 'joined';
    if (participant.declinedAt) return 'declined';
    return 'missed';
};

export const groupCallParticipantStatusMessage = (status: GroupCallParticipantStatus): MessageDescriptor => STATUS_MESSAGES[status];

export const groupCallJoinedParticipants = (call: Pick<Call, 'participants' | 'user'>): CallParticipant[] =>
    (call.participants ?? []).filter(participant => {
        const status = groupCallParticipantStatus(call, participant);
        return status === 'started' || status === 'joined';
    });

export const groupCallDuration = (call: Pick<Call, 'bridgetAt' | 'finishedAt'>): string | null =>
    call.bridgetAt && call.finishedAt ? formatGroupCallDuration({startedAt: call.bridgetAt, endedAt: call.finishedAt}) : null;

export const groupCallParticipantName = (participant: Pick<CallParticipant, 'firstName' | 'lastName'>): string | undefined => {
    const name = [participant.firstName, participant.lastName].filter(part => !!part?.trim()).join(' ');
    return name || undefined;
};
